/**
 * The camera the graph never had.
 *
 * There was no wheel handler and no drag handler on that canvas. The camera
 * was written in exactly one place, inside the render loop, and it re-fitted
 * the entire graph to the viewport on every frame:
 *
 *     target = min(w / boundsWidth, h / boundsHeight, 1.25)
 *     cam.s += (target - cam.s) * 0.06
 *
 * Two consequences followed, and both were reported as the graph being
 * broken. Expanding a node made everything SMALLER, necessarily, because the
 * bounding box grew and the fit shrank to hold it. And the scale eased toward
 * a moving target every frame, so the canvas never rendered text at a settled
 * size and nothing ever looked sharp.
 *
 * So: the operator drives, the page does not. A fit is offered as an action
 * and taken on first load; after that the camera moves only when someone moves
 * it. All of it is pure functions over `{x, y, s}` so the arithmetic that
 * decides what is on screen can be tested rather than watched.
 */

export interface Camera {
  /** World point at the centre of the viewport. */
  x: number;
  y: number;
  /** World units per screen pixel, inverted: 1 is actual size. */
  s: number;
}

export interface Viewport {
  width: number;
  height: number;
}

export interface Bounds {
  minX: number;
  maxX: number;
  minY: number;
  maxY: number;
}

/**
 * Below this a label is smaller than the type floor and the picture is a
 * texture rather than a diagram; above it, one node fills the screen.
 */
export const MIN_SCALE = 0.1;
export const MAX_SCALE = 4;

export function clampScale(scale: number): number {
  // NaN is a broken number and has no direction to clamp toward, so it falls
  // back to actual size. An infinity does have one and clamps like any other
  // out-of-range value, which is what a runaway zoom gesture produces.
  if (Number.isNaN(scale)) return 1;
  return Math.min(MAX_SCALE, Math.max(MIN_SCALE, scale));
}

/** Screen pixel to the world point under it. */
export function screenToWorld(
  camera: Camera,
  point: { x: number; y: number },
  viewport: Viewport,
): { x: number; y: number } {
  return {
    x: (point.x - viewport.width / 2) / camera.s + camera.x,
    y: (point.y - viewport.height / 2) / camera.s + camera.y,
  };
}

/** World point to the screen pixel that shows it. */
export function worldToScreen(
  camera: Camera,
  point: { x: number; y: number },
  viewport: Viewport,
): { x: number; y: number } {
  return {
    x: (point.x - camera.x) * camera.s + viewport.width / 2,
    y: (point.y - camera.y) * camera.s + viewport.height / 2,
  };
}

/**
 * Zoom about a screen point, keeping whatever is under the cursor under it.
 *
 * This is the whole of why a zoom feels right or feels like the page is
 * fighting you. Scaling about the viewport centre throws away the thing the
 * operator was pointing at, which on a wide tree means the node they were
 * reading leaves the screen the moment they lean in.
 */
export function zoomAt(
  camera: Camera,
  factor: number,
  pointer: { x: number; y: number },
  viewport: Viewport,
): Camera {
  const scale = clampScale(camera.s * factor);
  // Clamped away: the world point stays where it is and only the scale is
  // refused, rather than the view drifting on a zoom that could not happen.
  if (scale === camera.s) return camera;
  const before = screenToWorld(camera, pointer, viewport);
  const after = screenToWorld({ ...camera, s: scale }, pointer, viewport);
  return {
    s: scale,
    x: camera.x + (before.x - after.x),
    y: camera.y + (before.y - after.y),
  };
}

/** Drag: screen pixels moved, in world units, against the direction of travel. */
export function panBy(camera: Camera, dx: number, dy: number): Camera {
  return { ...camera, x: camera.x - dx / camera.s, y: camera.y - dy / camera.s };
}

/**
 * The camera that shows all of it, once.
 *
 * `padding` is in SCREEN pixels and is the room the labels need: a node's text
 * overhangs its dot by roughly half the label's width, so a fit computed on
 * node centres alone clips every name at the edges of the tree.
 */
export function fit(bounds: Bounds, viewport: Viewport, padding: number): Camera {
  const width = Math.max(bounds.maxX - bounds.minX, 1);
  const height = Math.max(bounds.maxY - bounds.minY, 1);
  const usableWidth = Math.max(viewport.width - padding * 2, 1);
  const usableHeight = Math.max(viewport.height - padding * 2, 1);
  return {
    x: (bounds.minX + bounds.maxX) / 2,
    y: (bounds.minY + bounds.maxY) / 2,
    // Never magnifies past actual size on a small graph: three nodes blown up
    // to fill a screen reads as a fault rather than as three nodes.
    s: clampScale(Math.min(usableWidth / width, usableHeight / height, 1)),
  };
}

/**
 * A wheel notch to a scale factor.
 *
 * Browsers report wheel deltas in three different units and a trackpad sends
 * a continuous stream of small ones, so a fixed step per event zooms a
 * trackpad about twenty times faster than a mouse. Exponentiating the delta
 * makes the two feel the same and makes zoom symmetric: in and back out by the
 * same gesture returns to the same scale.
 */
export function wheelFactor(deltaY: number, deltaMode: number): number {
  // 0 = pixels, 1 = lines, 2 = pages.
  const pixels = deltaMode === 1 ? deltaY * 16 : deltaMode === 2 ? deltaY * 400 : deltaY;
  return Math.exp(-Math.max(-200, Math.min(200, pixels)) / 400);
}
